// Calls TV APIs (window.tizen / window.webapis) by path:
//   call('tizen.tvaudiocontrol.getVolume')
//   call('tizen.application.getAppsInfo')   → callback-based APIs are turned into Promises
// Results are copied into JSON-safe plain objects (Tizen objects use getters).

/** For callback-based APIs: the argument index of the success callback (the error callback comes right after). */
const CALLBACK_AT: Record<string, number> = {
  'tizen.application.getAppsInfo': 0,
  'tizen.application.getAppsContext': 0,
  'tizen.application.launch': 1,
  'tizen.application.launchAppControl': 2,
  'tizen.application.findAppControl': 1,
  'tizen.tvchannel.getChannelList': 0,
  'tizen.tvchannel.getProgramList': 2,
  'tizen.tvchannel.getFavorites': 0,
  'tizen.tvwindow.getAvailableWindows': 0,
  'tizen.tvwindow.setSource': 1,
  'tizen.tvwindow.show': 0,
  'tizen.tvwindow.hide': 0,
  'tizen.tvwindow.getVideoResolution': 0,
  'tizen.tvdisplaycontrol.getSupported3DEffectModeList': 0,
  'tizen.systeminfo.getPropertyValue': 1,
  'tizen.systeminfo.getPropertyValueArray': 1,
  'tizen.content.getDirectories': 0,
  'tizen.content.find': 0,
  'tizen.filesystem.listStorages': 0,
  'tizen.package.getPackagesInfo': 0,
  'tizen.package.getPackageInfo': 0,
};

const PATH_RE = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)+$/;

/** Unprefixed paths like 'tvaudiocontrol.getVolume': try tizen. first, then webapis. */
function qualify(api: string) {
  if (/^(tizen|webapis)\./.test(api)) return api;
  const w = window as any;
  const head = api.split('.')[0];
  if (w.tizen?.[head] !== undefined) return `tizen.${api}`;
  if (w.webapis?.[head] !== undefined) return `webapis.${api}`;
  throw new Error(`Not available on this TV: ${api} (tizen.${api} / webapis.${api})`);
}

/** Copy Tizen objects into plain JSON (for-in also sees getters). */
export function plain(v: unknown, depth = 0): unknown {
  if (v == null || typeof v !== 'object') return typeof v === 'function' ? undefined : v;
  if (depth > 5) return undefined;
  if (Array.isArray(v)) return v.map((x) => plain(x, depth + 1));
  if (v instanceof Date) return v.toISOString();
  const out: Record<string, unknown> = {};
  for (const k in v as Record<string, unknown>) {
    try {
      const x = (v as Record<string, unknown>)[k];
      if (typeof x !== 'function') out[k] = plain(x, depth + 1);
    } catch {}
  }
  return out;
}

export async function callTvApi(rawApi: string, args: unknown[]): Promise<unknown> {
  if (!PATH_RE.test(rawApi)) throw new Error(`Invalid API path: ${rawApi}`);
  const api = qualify(rawApi);
  const parts = api.split('.');
  let parent: any = window;
  for (let i = 0; i < parts.length - 1; i++) {
    parent = parent?.[parts[i]];
    if (parent == null) throw new Error(`Not available on this TV: ${parts.slice(0, i + 1).join('.')}`);
  }
  const fn = parent[parts[parts.length - 1]];
  if (typeof fn !== 'function') {
    if (args.length) throw new Error(`${api} is not a function`);
    return plain(fn);
  }
  const at = CALLBACK_AT[api];
  if (at === undefined) return plain(fn.apply(parent, args));
  return new Promise((resolve, reject) => {
    const a = args.slice();
    while (a.length < at) a.push(undefined);
    a.splice(at, 0, (r: unknown) => resolve(plain(r)), (e: { name?: string; message?: string }) => reject(new Error(`${e?.name || 'Error'}: ${e?.message || ''}`)));
    try {
      fn.apply(parent, a);
    } catch (e) {
      reject(e);
    }
  });
}
